import { Input } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select, SelectOption } from '@/components/ui/Select';
import { useUsers } from '@/hooks/useUsers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TicketAppointment } from './TicketForm';
import type { Dispatch, SetStateAction } from 'react';

const timeOptions: SelectOption[] = [
  { value: 'minutes', content: 'm' },
  { value: 'hours', content: 'h' },
];

interface TicketFormAppointmentProps {
  appointment: Partial<TicketAppointment>;
  onAppointmentChange: Dispatch<SetStateAction<Partial<TicketAppointment>>>;
}

export function TicketFormAppointment({
  appointment,
  onAppointmentChange,
}: TicketFormAppointmentProps) {
  const { t } = useTranslation();

  const { users } = useUsers({ limit: 100 }); // Fetch more users for select options

  // Get user options from users (ADMIN and TIME_TRACKER only)
  const userOptions: SelectOption[] = useMemo(() => {
    return users
      .filter((user) => user.role === 'ADMIN' || user.role === 'TIME_TRACKER')
      .filter((user) => user.isActive)
      .map((user) => ({
        value: user.id,
        content: user.name,
      }));
  }, [users]);

  return (
    <div className="gap-2xs grid grid-cols-[1fr_1fr_1fr_60px_60px]">
      <DatePicker
        placeholder={t('tickets.form.datePlaceholder')}
        value={appointment.date}
        onChange={(date) => onAppointmentChange((prev) => ({ ...prev, date: date as Date }))}
      />
      <Select
        options={userOptions}
        placeholder={t('tickets.form.appointmentUserPlaceholder')}
        value={appointment.userId}
        onValueChange={(value) => onAppointmentChange((prev) => ({ ...prev, userId: value }))}
      />
      <Input
        type="number"
        placeholder={t('tickets.form.workedTimePlaceholder')}
        className="flex-1"
        value={appointment.workedTime?.toString() || ''}
        onChange={(e) =>
          onAppointmentChange((prev) => ({ ...prev, workedTime: Number(e.target.value) }))
        }
      />
      <Select
        options={timeOptions}
        width={60}
        value={appointment.timeUnit || 'hours'}
        onValueChange={(value) =>
          onAppointmentChange((prev) => ({ ...prev, timeUnit: value as 'hours' | 'minutes' }))
        }
      />
      <Checkbox
        name="isDoubleDebited"
        label={t('tickets.form.isDoubleDebited')}
        checked={appointment.isDoubleDebited || false}
        onCheckedChange={(checked) =>
          onAppointmentChange((prev) => ({ ...prev, isDoubleDebited: checked === true }))
        }
      />
    </div>
  );
}
